import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlatformApiError, type MediaObject, type Viewer } from '@sfc/sdk';
import { platform } from '../api/client';
import { Empty } from '../ui/Empty';
import { Icon } from '../ui/Icon';
import { StatStrip } from '../ui/StatStrip';

/** The same ceiling the API enforces, so the refusal happens before the upload. */
const MAX_BYTES = 10 * 1024 * 1024;

const ACCEPTED = '.pdf,.doc,.docx';

interface DocumentsPanelProps {
  viewer: Viewer;
}

/**
 * Private documents.
 *
 * Downloading goes through the API rather than a link into the object store, so
 * what the browser holds is a blob it fetched with the session it already has.
 * Nothing here can be copied out of the address bar and used by somebody else.
 */
export function DocumentsPanel({ viewer }: DocumentsPanelProps): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const mayWrite = viewer.capabilities.includes('documents:write');
  const mayDelete = viewer.capabilities.includes('documents:delete');

  const [documents, setDocuments] = useState<MediaObject[] | undefined>();
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setDocuments(await platform.documents.list(organizationId, signal));
      } catch (error) {
        if (signal?.aborted) return;
        setDocuments([]);
        setProblem(describe(error));
      }
    },
    [organizationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const stats = useMemo(() => {
    const rows = documents ?? [];
    const bytes = rows.reduce((total, row) => total + row.byteSize, 0);
    const attached = rows.filter((row) => row.attached).length;
    return [
      { label: 'Documents', value: rows.length },
      { label: 'Attached to a record', value: attached },
      { label: 'Stored', value: formatSize(bytes) },
    ];
  }, [documents]);

  async function upload(file: File): Promise<void> {
    setProblem(undefined);

    // Refused here as well as by the API. A person should be told immediately
    // rather than after sending ten megabytes up a slow connection.
    if (file.size > MAX_BYTES) {
      setProblem('Files must be 10 MiB or smaller.');
      return;
    }

    setBusy(true);
    try {
      await platform.documents.upload(organizationId, file);
      await load();
      if (fileInput.current) fileInput.current.value = '';
    } catch (error) {
      setProblem(describe(error));
    } finally {
      setBusy(false);
    }
  }

  async function download(document_: MediaObject): Promise<void> {
    setProblem(undefined);
    try {
      const blob = await platform.documents.download(organizationId, document_.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = document_.fileName;
      link.click();
      // The blob is the browser's copy; release it once the save has started.
      URL.revokeObjectURL(url);
    } catch (error) {
      setProblem(describe(error));
    }
  }

  async function remove(document_: MediaObject): Promise<void> {
    setProblem(undefined);
    try {
      await platform.documents.remove(organizationId, document_.id);
      await load();
    } catch (error) {
      setProblem(describe(error));
    }
  }

  return (
    <section className="card" aria-labelledby="section-heading">
      <StatStrip stats={stats} testId="document-stats" />

      {mayWrite ? (
        <div className={`dropzone${busy ? ' dropzone--busy' : ''}`}>
          <span className="dropzone__icon">
            <Icon name="upload" size={20} />
          </span>
          <div className="dropzone__text">
            <label htmlFor="document-file">Add a document</label>
            <span className="row__sub">{busy ? 'Uploading…' : 'PDF or Word, up to 10 MiB. It stays private.'}</span>
          </div>
          <input
            id="document-file"
            ref={fileInput}
            type="file"
            accept={ACCEPTED}
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
            }}
          />
        </div>
      ) : null}

      {problem ? (
        <p className="notice notice--problem" role="alert" data-testid="documents-problem">
          {problem}
        </p>
      ) : null}

      <div className="panel">
        <div className="panel__head">
          <h2>Stored files</h2>
          <span className="count" data-testid="document-count">
            {documents ? `${documents.length}` : '…'}
          </span>
        </div>

        {documents && documents.length === 0 ? (
          <Empty
            icon="documents"
            title="Nothing stored yet"
            hint="Upload a PDF or Word file. It stays private and is never reachable by a URL."
          />
        ) : null}

        <ul className="ledger" data-testid="documents">
          {(documents ?? []).map((document_) => (
            <li key={document_.id} className="ledger__row ledger__row--file" data-testid={`document-${document_.fileName}`}>
              <span className="file__icon">
                <Icon name="documents" />
              </span>
              <div className="ledger__id">
                <span className="row__title">{document_.fileName}</span>
                <span className="row__sub">
                  {formatSize(document_.byteSize)}, {document_.contentType}
                  {document_.attached ? ', attached to an application' : ''}
                </span>
              </div>
              <span className="button-row">
                <button type="button" className="button button--small" onClick={() => void download(document_)}>
                  <Icon name="download" size={16} />
                  Download
                </button>
                {mayDelete && !document_.attached ? (
                  <button
                    type="button"
                    className="button button--small button--danger"
                    aria-label={`Remove ${document_.fileName}`}
                    onClick={() => void remove(document_)}
                  >
                    Remove
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function describe(error: unknown): string {
  if (error instanceof PlatformApiError) {
    if (error.status === 413) return 'Files must be 10 MiB or smaller.';
    if (error.status === 415) return error.message;
    if (error.status === 403) return 'Your role may not do that.';
    if (error.status === 404) return 'That file is no longer there.';
  }
  return 'Something went wrong.';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
